import { z } from 'zod';

// New schemas for additional context fields
export const MemoryUsage = z.object({
  rss: z.number(),
  heap_total: z.number(),
  heap_used: z.number(),
  external: z.number(),
  system_memory: z.number(),
  rss_percentage: z.number(),
});
export type MemoryUsage = z.infer<typeof MemoryUsage>;

export const ObservabilityIdentifiers = z.object({
  otel_trace_id: z.string().optional(),
  datadog_trace_id: z.string().optional(),
  amazon_trace_id: z.string().optional(),
});
export type ObservabilityIdentifiers = z.infer<typeof ObservabilityIdentifiers>;

export const SystemInfo = z.object({
  pod_name: z.string().optional(),
  node_name: z.string().optional(),
});
export type SystemInfo = z.infer<typeof SystemInfo>;

export const CPUUsage = z.object({
  user_time: z.number(),
  system_time: z.number(),
  limit: z.union([z.number(), z.literal('unlimited')]).optional(),
  user_percent_of_limit: z.number().optional(),
  system_percent_of_limit: z.number().optional(),
});
export type CPUUsage = z.infer<typeof CPUUsage>;

export const MachineMetrics = z.object({
  cpu_usage: CPUUsage.optional(),
  event_loop_utilization: z.number().optional(),
  memory_usage: MemoryUsage.optional(),
});
export type MachineMetrics = z.infer<typeof MachineMetrics>;

export const BaseInvestigationContext = z.object({
  type: z
    .union([z.literal('http'), z.literal('sqs')])
    .optional()
    .default('http'),
  system_info: SystemInfo.optional(),
  machine_metrics: MachineMetrics.optional(),
  observability_identifiers: ObservabilityIdentifiers.optional(),
});
export type BaseInvestigationContext = z.infer<typeof BaseInvestigationContext>;

export const ExecutionFlowItem = z.object({
  caller_id: z.string().optional(),
  function_id: z.string(),
});
export type ExecutionFlowItem = z.infer<typeof ExecutionFlowItem>;

export const ResolvedExecutionFlowItem = z.object({
  caller: z.string().optional(),
  function: z.string(),
});
export type ResolvedExecutionFlowItem = z.infer<typeof ResolvedExecutionFlowItem>;

export const ExceptionInfo = z.object({
  executionFlow: z.array(ExecutionFlowItem),
  message: z.string().optional().default(''),
  name: z.string(),
  stackTrace: z.string().optional(),
});
export type ExceptionInfo = z.infer<typeof ExceptionInfo>;

export const ResolvedExceptionInfo = z.object({
  executionFlow: z.array(ResolvedExecutionFlowItem),
  message: z.string().optional().default(''),
  name: z.string(),
  stackTrace: z.string().optional(),
});
export type ResolvedExceptionInfo = z.infer<typeof ResolvedExceptionInfo>;

// Extend BaseInvestigationContext for HTTP
export const HttpInvestigationContext = BaseInvestigationContext.extend({
  type: z.literal('http').optional().default('http'),
  method: z.string(),
  params: z.unknown(),
  query: z.unknown(),
  request_body: z.unknown(),
  route: z.string(),
  status_code: z.number(),
});
export type HttpInvestigationContext = z.infer<typeof HttpInvestigationContext>;

// Extend BaseInvestigationContext for SQS
export const SqsInvestigationContext = BaseInvestigationContext.extend({
  type: z.literal('sqs'),
  flow_id: z.string(),
  messages: z.array(z.record(z.string(), z.unknown())),
});
export type SqsInvestigationContext = z.infer<typeof SqsInvestigationContext>;

export const S3EndpointInvestigation = z.object({
  context: HttpInvestigationContext,
  exceptions: z.array(ExceptionInfo),
  version: z.string().optional().default(''),
});
export type S3EndpointInvestigation = z.infer<typeof S3EndpointInvestigation>;

export const S3SqsInvestigation = z.object({
  context: SqsInvestigationContext,
  exceptions: z.array(ExceptionInfo),
  version: z.string(),
});
export type S3SqsInvestigation = z.infer<typeof S3SqsInvestigation>;

export type S3Investigation = S3EndpointInvestigation | S3SqsInvestigation;

export function isS3EndpointInvestigation(
  investigation: S3EndpointInvestigation | S3SqsInvestigation,
): investigation is S3EndpointInvestigation {
  return investigation.context.type === 'http';
}

export function isS3SqsInvestigation(
  investigation: S3EndpointInvestigation | S3SqsInvestigation,
): investigation is S3SqsInvestigation {
  return investigation.context.type === 'sqs';
}
